import {
  Box,
  Container,
  IconButton,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  Divider,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { useDispatch, useSelector } from 'react-redux';
import DateDropZone from '../DropZones/DateDropZone';
import TextField from '@mui/material/TextField';
import {
  addSource,
  deleteSourceV3,
  resetSource,
  setSource,
} from '../../../redux/Calculations/CalculationsActions';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import FormatBuilder from './FormatBuilder';
import { fontSize } from '../../..';
import { RootState } from '../../../redux';
import LockOutlinedIcon from '@mui/icons-material/Lock';
import { checkIfMissingColumn } from '../../CommonFunctions/CommonFunctions';

interface IStringToDate {
  propKey: string;
  isFlowUsedInOtherFlowsAsSource: boolean;
}

const StringToDate = ({ propKey, isFlowUsedInOtherFlowsAsSource }: IStringToDate) => {
  const { calculations, tabTileProps, chartProperties } = useSelector((state: RootState) => {
    return {
      calculations: state.calculations,
      tabTileProps: state.tabTileProps,
      chartProperties: state.chartProperties,
    };
  });
  const dispatch = useDispatch();
  const currCalculation = calculations.properties[propKey];
  const valueTypes = useMemo(() => ['text', 'decimal', 'integer', 'boolean', 'date'], []);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [isShowWarning, setIsShowWarning] = useState<boolean>(false);

  const selectedDs = chartProperties.properties[propKey].selectedDs;
  const activeFlow = currCalculation.currentCalculationSession?.activeFlow!;
  const calculationInfo = currCalculation.currentCalculationSession?.calculationInfo!;
  const source = calculationInfo?.flows[activeFlow][0].source;
  const sourceType = calculationInfo?.flows[activeFlow][0]!.sourceType;
  const [name, setName] = useState<string>(source[0] || '');
  const getName = (): string | null => {
    if (sourceType[0] === 'field') return calculationInfo.fields[source[0]].displayName;
    else if (sourceType[0] === 'flow') {
      if (!currCalculation.currentCalculationSession) return null;

      return currCalculation.currentCalculationSession!.flowDisplayNames[source[0]];
    }
    return source[0];
  };
  const updateFormatValue = (idx: number | undefined, value: any, _delete: boolean = false) => {
    const new_idx = idx !== undefined ? idx + 1 : idx;
    if (_delete && new_idx) dispatch(deleteSourceV3(propKey, activeFlow, 0, new_idx));
    else dispatch(addSource(propKey, activeFlow, 0, value, 'text', new_idx));
  };
  const getSeperator = (source: any): string[] => {
    const exclude = ['%Y', '%y', '%M', '%b', '%c', '%m', '%e', '%d', '%D'];
    return source.slice(1).filter((value: string) => !exclude.includes(value));
  };

  useEffect(() => {
    if (sourceType[0] !== 'field') return;
    (async () => {
      try {
        const field = calculationInfo?.fields[source[0]];
        const res = await checkIfMissingColumn(
          field,
          selectedDs,
          tabTileProps.tablesForSelectedDataSets[selectedDs.id],
        );
        setIsShowWarning(res.isMissing);
      } catch (error) {
        console.error('Error checking missing column:', error);
        setIsShowWarning(true);
      }
    })();
  }, [sourceType]);

  return (
    <Container
      disableGutters
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        paddingLeft: '0.5rem',
        margin: 0,
      }}
      maxWidth="xs"
    >
      <Box
        sx={{
          padding: '0',
          margin: '0',
          fontWeight: 'bold',
          // color: "gray",
          textAlign: 'start',
          display: 'flex',
          gap: '10px',
          justifyContent: 'space-between',
        }}
      >
        <Box
          sx={{
            fontWeight: 'bold',
            // color: "gray",
            width: 'fit-content',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              fontWeight: 'bold',
              fontSize: fontSize.large,
            }}
          >
            String to Date
          </Typography>
          <Typography
            sx={{
              textAlign: 'center',
              fontSize: '11px',
              color: '#999999',
              marginLeft: '0.3rem',
              fontWeight: 'bold',
            }}
          >
            ({source && source[0] !== undefined && source[0] !== null ? 1 : 0} of 1/1)
          </Typography>
        </Box>
        <Tooltip
          title="Add Custom Date"
          slotProps={{
            tooltip: {
              sx: {
                fontSize: '12px',
              },
            },
          }}
        >
          <IconButton
            size="small"
            onClick={(e) => {
              setAnchorEl(e.currentTarget);
            }}
            disabled={isFlowUsedInOtherFlowsAsSource}
          >
            <AddCircleOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Menu open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}>
          {valueTypes.map((type, index) => {
            return (
              <MenuItem
                key={index}
                sx={{ width: '7rem' }}
                disabled={type !== 'text'}
                value={type}
                onClick={() => {
                  setAnchorEl(null);
                  dispatch(addSource(propKey, activeFlow, 0, '', 'text', 0));
                }}
              >
                {type.replace(/\b\w/g, (char) => char.toUpperCase())}
              </MenuItem>
            );
          })}
        </Menu>
      </Box>
      <DateDropZone
        propKey={propKey}
        flow="stringToDate"
        disableDrop={isFlowUsedInOtherFlowsAsSource}
        sx={{
          ...(![null, undefined].includes(source[0]) ? { minHeight: '5rem', flexGrow: 0 } : {}),
        }}
      >
        {source[0] === null || source[0] === undefined ? (
          <p style={{ fontSize: '11px', color: '#999999', textAlign: 'center' }}>
            Drop a column or click + <br />
            to add static values
          </p>
        ) : (
          <ListItem
            className="axisFilterField"
            sx={{
              borderRadius: '5px',
              border: isShowWarning ? '1px solid #850000ff !important' : '',
              marginBlock: '5px',
              marginLeft: '0.5rem',
              paddingLeft: '0.3rem',
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              height: '24px', // Match this height with TextField height if needed
              width: '95%',
              margin: '0',
              gap: '0.2rem',
              fontFamily: 'Axiforma Black',
            }}
          >
            {!isFlowUsedInOtherFlowsAsSource && (
              <button
                type="button"
                className="buttonCommon columnClose"
                onClick={() => {
                  dispatch(resetSource(propKey, activeFlow, 0));
                  setName('');
                }}
                title="Remove field"
              >
                <CloseRoundedIcon style={{ fontSize: '13px' }} />
              </button>
            )}
            {isFlowUsedInOtherFlowsAsSource && (
              <Tooltip
                title="This calculation is being used in another flow. Please remove the dependency before deleting."
                slotProps={{
                  tooltip: {
                    sx: {
                      fontSize: '12px',
                    },
                  },
                }}
              >
                <LockOutlinedIcon
                  style={{
                    color: '#2BB9BB',
                  }}
                />
              </Tooltip>
            )}
            {!isFlowUsedInOtherFlowsAsSource && sourceType[0] === 'text' ? (
              <TextField
                id="outlined-number"
                onBlur={(e) => {
                  dispatch(setSource(propKey, activeFlow, 0, e.target.value, 'text', 0));
                }}
                onKeyDown={(e) => {
                  console.log(name);
                  if (e.key === 'Enter') {
                    dispatch(setSource(propKey, activeFlow, 0, name, 'text', 0));
                  }
                }}
                onChange={(e) => setName(e.target.value)}
                value={name}
                type="text"
                sx={{
                  width: '100%',
                  marginLeft: '0.5rem',
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      border: 'none',
                    },
                  },
                  '& .MuiInputBase-root': {
                    height: '1.5rem',
                    fontSize: '11px',
                  },
                  '& .MuiTextField-root': {
                    outline: 'none',
                    border: 'none',
                  },
                  border: 'none',
                  outline: 'none',
                }}
              />
            ) : sourceType[0] === 'field' || sourceType[0] === 'flow' ? (
              <ListItemText
                primary={getName()}
                sx={{ '& .MuiListItemText-primary': { fontSize: '11px' } }}
              />
            ) : null}
          </ListItem>
        )}
      </DateDropZone>
      <Divider />
      {source[0] !== null && source[0] !== undefined && (
        <FormatBuilder
          updateFormatValue={updateFormatValue}
          yearFormat={source[1]}
          monthFormat={source[3]}
          dayFormat={source[5]}
          separator={getSeperator(source)}
          sources={source.slice(1)}
          isFlowUsedInOtherFlowsAsSource={isFlowUsedInOtherFlowsAsSource}
        />
      )}
    </Container>
  );
};

export default StringToDate;
